import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { router } from './router'
import { queryClient } from './lib/query-client'
import { ThemeProvider } from './lib/theme'
import { AuthProvider } from './lib/auth'
import './index.css'

// Token expired / rejected (401 from any API call). Clear the stored token and
// the per-tab "already tried silent SSO" guards, then bounce to /login. Clearing
// the token is what breaks the redirect loop: without it, /login sees the token
// still present, thinks it's authenticated, and sends the user straight back to
// the dashboard — which 401s again. The in-flight flag collapses the burst of
// 401s from parallel dashboard queries into a single navigation.
let _reauthInFlight = false
window.addEventListener('aip-dns:auth-expired', () => {
  if (_reauthInFlight) return
  _reauthInFlight = true
  localStorage.removeItem('aip_dns_access_token')
  sessionStorage.removeItem('aip_dns_silent_attempt')
  // NOTE: intentionally do NOT clear 'aip_dns_silent_failed'. If the backend
  // rejects a freshly-issued token (audience/type mismatch rather than real
  // expiry), re-attempting silent SSO would just fetch another rejected token
  // and loop via full-page redirects. Leaving the guard set means the user
  // lands on the manual sign-in button after one round instead of looping.
  router.navigate({ to: '/login' }).finally(() => {
    setTimeout(() => { _reauthInFlight = false }, 1000)
  })
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
)
